import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import { expectedAnswer } from "../Quiz/answer.ts";
import { cardFaces } from "../cardFaces.ts";

/*
 * Slingshot Plate Smash (ADR 0060): a carnival booth where the answer is on
 * one of four plates and the others carry near misses.
 *
 * Pure, like dropGame.ts - no timers, no DOM, no React. The component feeds
 * `advance` animation-frame deltas and `fire` slingshot releases. Randomness
 * (which plate gets the answer, which near misses are used) comes in as
 * `randInt`, inclusive at both ends like `getRandomInt`, so tests can fix it.
 *
 * The booth is 100 units wide and 75 tall. Plates sit on a shelf at y 26; the
 * slingshot's fork is at (50, 58).
 */

export type Mode = "easy" | "hard";
export type RandInt = (min: number, max: number) => number;

export const ROUND_MS = 60_000;
export const COUNTDOWN_MS = 3000;
export const MAX_FRAME_MS = 250;
/** How long a rock is in the air. The hit is decided where the plates are on arrival (§1). */
export const FLIGHT_MS = 550;
export const PLATE_COUNT = 4;
/** A rock landing within this distance of a plate's centre breaks it. */
export const PLATE_HALF_WIDTH = 7;
export const BOOTH_WIDTH = 100;
export const SHELF_Y = 26;
export const FORK = { x: 50, y: 58 };

export const POINTS = 10;
/** From this many first-shot hits in a row, each is worth double (§4). */
export const HOT_STREAK = 3;

/** Easy mode: evenly spaced and still. */
export const EASY_SLOTS = [17, 39, 61, 83];
/**
 * Hard mode: the belt loop runs from -10 to 110, and a plate crosses the booth
 * in 5 s - fast enough that a plate moves further during a rock's flight (11
 * units) than its own half-width, so a shot aimed where it *is* misses (§3).
 */
const BELT_START = -10;
const BELT_LENGTH = 120;
export const BELT_SPEED = BOOTH_WIDTH / 5000;
const EFFECT_MS = 700;

export interface Plate {
    /** Fixed position index, so a plate keeps its place when relabelled (§3). */
    slot: number;
    value: number;
    broken: boolean;
}

export interface Rock {
    id: number;
    targetX: number;
    firedAtMs: number;
}

/** Shards where a plate broke - decoration, kept briefly for the component to draw. */
export interface Shatter {
    id: number;
    x: number;
    value: number;
    right: boolean;
    atMs: number;
}

export interface PlateMiss {
    front: string;
    answer: string;
    /** The wrong plate that was broken. */
    picked: number;
}

export type PlateEvent =
    | { kind: "hit"; seq: number; value: number; points: number }
    | { kind: "late"; seq: number; value: number }
    | { kind: "wrong"; seq: number; value: number }
    | { kind: "miss"; seq: number };

export interface PlateState {
    phase: "countdown" | "playing" | "over";
    mode: Mode;
    countdownLeftMs: number;
    /** Play time since the round started; the belt and the clock both read it. */
    playMs: number;
    problem: ProblemData;
    plates: Plate[];
    rock: Rock | null;
    score: number;
    /** First-shot hits in a row. */
    streak: number;
    /** No wrong plate has broken on this problem yet. */
    firstShot: boolean;
    hits: number;
    wrongPlates: number;
    misses: PlateMiss[];
    /** Problems hit with the first shot, as shown ("7 x 8") - for the missed-facts store. */
    correctFronts: string[];
    shatters: Shatter[];
    lastEvent: PlateEvent | null;
    nextId: number;
}

/**
 * Three wrong answers a child who half-knows the fact would not rule out at a
 * glance (§2): neighbouring facts, slips of one or ten, and for division the
 * divisor. Whole numbers only, never the answer, never repeated. Topped up
 * with the nearest unused numbers when a small answer leaves too few.
 */
export function distractorsFor(problem: ProblemData, randInt: RandInt, count = PLATE_COUNT - 1): number[] {
    const [a, b] = problem.numbers;
    const answer = expectedAnswer(problem);
    const near = [answer - 1, answer + 1, answer - 2, answer + 2, answer + 10, answer - 10];
    let candidates: number[];
    switch (problem.operator) {
        case "x":
            candidates = [a * (b + 1), a * (b - 1), (a + 1) * b, (a - 1) * b, answer + 1, answer - 1, answer + 10];
            break;
        case "÷":
            candidates = [answer - 1, answer + 1, b, answer + 2, answer - 2];
            break;
        case "-":
            candidates = [...near, a + b];
            break;
        default:
            candidates = near;
    }

    const pool = Array.from(new Set(candidates.filter((n) => Number.isInteger(n) && n >= 0 && n !== answer)));
    const chosen: number[] = [];
    while (chosen.length < count && pool.length > 0) {
        chosen.push(pool.splice(randInt(0, pool.length - 1), 1)[0]);
    }
    for (let step = 1; chosen.length < count; step++) {
        for (const n of [answer + step, answer - step]) {
            if (chosen.length < count && n >= 0 && !chosen.includes(n)) {
                chosen.push(n);
            }
        }
    }
    return chosen;
}

/** The answer and three near misses, in a random order across the slots. */
export function dealPlates(problem: ProblemData, randInt: RandInt): Plate[] {
    const values = [expectedAnswer(problem), ...distractorsFor(problem, randInt)];
    for (let i = values.length - 1; i > 0; i--) {
        const j = randInt(0, i);
        [values[i], values[j]] = [values[j], values[i]];
    }
    return values.map((value, slot) => ({ slot, value, broken: false }));
}

/** Where a plate is now. Easy plates stay put; hard plates ride the belt. */
export function plateX(mode: Mode, slot: number, playMs: number): number {
    if (mode === "easy") {
        return EASY_SLOTS[slot];
    }
    const travelled = (slot * BELT_LENGTH) / PLATE_COUNT + playMs * BELT_SPEED;
    return BELT_START + (((travelled % BELT_LENGTH) + BELT_LENGTH) % BELT_LENGTH);
}

/**
 * Where a rock lands, from where the pouch was pulled to. The rock leaves
 * through the fork, opposite the pull - pull left, it goes right - and carries
 * on in a straight line to the shelf. Null when the pull is too short or not
 * backwards, which cancels the shot (§1).
 */
export function aimFromPull(pouch: { x: number; y: number }): number | null {
    const dx = FORK.x - pouch.x;
    const dy = FORK.y - pouch.y;
    if (!Number.isFinite(dx) || !Number.isFinite(dy) || dy > -3) {
        return null;
    }
    const t = (SHELF_Y - FORK.y) / dy;
    return clampAim(FORK.x + dx * t);
}

export function clampAim(x: number): number {
    return Math.min(BOOTH_WIDTH, Math.max(0, x));
}

export function startGame(mode: Mode, queue: QuizQueue, randInt: RandInt): PlateState {
    const problem = queue.next();
    return {
        phase: "countdown",
        mode,
        countdownLeftMs: COUNTDOWN_MS,
        playMs: 0,
        problem,
        plates: dealPlates(problem, randInt),
        rock: null,
        score: 0,
        streak: 0,
        firstShot: true,
        hits: 0,
        wrongPlates: 0,
        misses: [],
        correctFronts: [],
        shatters: [],
        lastEvent: null,
        nextId: 1,
    };
}

/** Lets a rock go. Ignored outside play, or while the last rock is still in the air. */
export function fire(state: PlateState, aimX: number): PlateState {
    if (state.phase !== "playing" || state.rock) {
        return state;
    }
    return {
        ...state,
        rock: { id: state.nextId, targetX: clampAim(aimX), firedAtMs: state.playMs },
        nextId: state.nextId + 1,
    };
}

/** The plate a rock at `x` would break right now, if any. */
export function plateAt(state: PlateState, x: number): Plate | null {
    let best: Plate | null = null;
    let bestDistance = PLATE_HALF_WIDTH;
    for (const plate of state.plates) {
        const distance = Math.abs(plateX(state.mode, plate.slot, state.playMs) - x);
        if (!plate.broken && distance <= bestDistance) {
            best = plate;
            bestDistance = distance;
        }
    }
    return best;
}

function land(state: PlateState, rock: Rock, queue: QuizQueue, randInt: RandInt): PlateState {
    const seq = rock.id;
    const plate = plateAt(state, rock.targetX);
    const base = { ...state, rock: null };
    if (!plate) {
        return { ...base, lastEvent: { kind: "miss", seq } };
    }

    const answer = expectedAnswer(state.problem);
    const faces = cardFaces(state.problem);
    const shatter: Shatter = {
        id: seq,
        x: plateX(state.mode, plate.slot, state.playMs),
        value: plate.value,
        right: plate.value === answer,
        atMs: state.playMs,
    };
    const shatters = [...base.shatters, shatter];

    if (plate.value !== answer) {
        // The plate is gone for this problem and the streak with it; the miss
        // is recorded once, however many wrong plates follow (§7).
        return {
            ...base,
            plates: state.plates.map((p) => (p === plate ? { ...p, broken: true } : p)),
            streak: 0,
            firstShot: false,
            wrongPlates: state.wrongPlates + 1,
            misses: state.firstShot
                ? [...state.misses, { front: faces.front, answer: faces.back, picked: plate.value }]
                : state.misses,
            shatters,
            lastEvent: { kind: "wrong", seq, value: plate.value },
        };
    }

    // The right plate: a new problem, relabelled where the plates stand (§3).
    const problem = queue.next();
    const plates = dealPlates(problem, randInt);
    if (!state.firstShot) {
        return { ...base, problem, plates, firstShot: true, shatters, lastEvent: { kind: "late", seq, value: plate.value } };
    }
    const streak = state.streak + 1;
    const points = streak >= HOT_STREAK ? POINTS * 2 : POINTS;
    return {
        ...base,
        problem,
        plates,
        firstShot: true,
        score: state.score + points,
        streak,
        hits: state.hits + 1,
        correctFronts: [...state.correctFronts, faces.front],
        shatters,
        lastEvent: { kind: "hit", seq, value: plate.value, points },
    };
}

/**
 * Moves play time on: the countdown, the round clock, the belt, and any rock
 * that has arrived. Draws from the queue when the right plate breaks, so the
 * caller must not run it inside a reducer (0053 §8).
 */
export function advance(state: PlateState, dtMs: number, queue: QuizQueue, randInt: RandInt): PlateState {
    const dt = Math.min(Math.max(dtMs, 0), MAX_FRAME_MS);
    if (dt === 0 || state.phase === "over") {
        return state;
    }
    if (state.phase === "countdown") {
        const left = state.countdownLeftMs - dt;
        return left > 0 ? { ...state, countdownLeftMs: left } : { ...state, phase: "playing", countdownLeftMs: 0 };
    }

    const playMs = Math.min(ROUND_MS, state.playMs + dt);
    let next: PlateState = { ...state, playMs };
    if (next.rock && playMs - next.rock.firedAtMs >= FLIGHT_MS) {
        next = land(next, next.rock, queue, randInt);
    }
    if (next.shatters.some((s) => playMs - s.atMs > EFFECT_MS)) {
        next = { ...next, shatters: next.shatters.filter((s) => playMs - s.atMs <= EFFECT_MS) };
    }
    if (playMs >= ROUND_MS) {
        // A rock still in the air when the bell goes does not count.
        next = { ...next, phase: "over", rock: null };
    }
    return next;
}

/** How far through its flight a rock is, 0 to 1. */
export function rockProgress(state: PlateState): number {
    return state.rock ? Math.min(1, (state.playMs - state.rock.firedAtMs) / FLIGHT_MS) : 0;
}
